import { db } from "./db";
import { exportEnvelopeSchema } from "./schemas";
import type { ExportEnvelope } from "../types";

type ImportMode = "merge" | "replace";

export type ImportPreview = {
  jobs: number;
  contractors: number;
  savedJobs: number;
  schemaVersion: ExportEnvelope["schemaVersion"];
  exportedAt: string;
};

const maxImportBytes = 5 * 1024 * 1024;

export async function buildExportEnvelope(): Promise<ExportEnvelope> {
  const [jobs, contractors, savedJobs, settings] = await Promise.all([
    db.jobs.toArray(),
    db.contractors.toArray(),
    db.savedJobs.toArray(),
    db.settings.get("local"),
  ]);
  const exportedAt = new Date().toISOString();

  if (settings) {
    await db.settings.put({ ...settings, lastExportAt: exportedAt, updatedAt: exportedAt });
  }

  return {
    schemaVersion: 2,
    exportedAt,
    app: "ClearScope",
    jobs,
    contractors,
    savedJobs,
    settings: settings
      ? { ...settings, lastExportAt: exportedAt, updatedAt: exportedAt }
      : undefined,
  };
}

export function migrateEnvelope(envelope: ExportEnvelope): ExportEnvelope {
  if (envelope.schemaVersion === 2) {
    return envelope;
  }

  return {
    ...envelope,
    schemaVersion: 2,
    settings: envelope.settings
      ? {
          ...envelope.settings,
          acknowledgedStaticLimitations: envelope.settings.acknowledgedStaticLimitations ?? false,
          aiDataAccess: envelope.settings.aiDataAccess ?? {
            jobs: false,
            riskFlags: false,
            contact: false,
          },
        }
      : undefined,
  };
}

export function parseExportEnvelope(raw: string): ExportEnvelope {
  if (new Blob([raw]).size > maxImportBytes) {
    throw new Error("Import file is larger than the 5 MB local safety limit.");
  }
  const parsed = JSON.parse(raw) as unknown;
  const safeEnvelope = exportEnvelopeSchema.parse(parsed) as ExportEnvelope;
  return migrateEnvelope(safeEnvelope);
}

export function previewEnvelope(envelope: ExportEnvelope): ImportPreview {
  return {
    jobs: envelope.jobs.length,
    contractors: envelope.contractors.length,
    savedJobs: envelope.savedJobs.length,
    schemaVersion: envelope.schemaVersion,
    exportedAt: envelope.exportedAt,
  };
}

export async function importEnvelope(
  envelope: ExportEnvelope,
  mode: ImportMode = "merge",
): Promise<void> {
  const safeEnvelope = exportEnvelopeSchema.parse(migrateEnvelope(envelope)) as ExportEnvelope;

  await db.transaction("rw", [db.jobs, db.contractors, db.savedJobs, db.settings], async () => {
    if (mode === "replace") {
      await Promise.all([db.jobs.clear(), db.contractors.clear(), db.savedJobs.clear()]);
    }
    await db.jobs.bulkPut(safeEnvelope.jobs);
    await db.contractors.bulkPut(safeEnvelope.contractors);
    await db.savedJobs.bulkPut(safeEnvelope.savedJobs);
    if (safeEnvelope.settings) {
      await db.settings.put(safeEnvelope.settings);
    }
  });
}
